import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Loader2, RefreshCcw } from "lucide-react";

/**
 * NUMBERS — one table, one denominator. No charts, no recommendations.
 * Labeled PRODUCTION on the deployed domain, PREVIEW DATA anywhere else.
 * The deploy-marker row splits old vs new first-visit experience.
 */
const COLS = [
    { k: "signups", label: "Signups" },
    { k: "via_demo", label: "Via demo card" },
    { k: "first_session_checkin", label: "First-session check-in taps" },
    { k: "intake", label: "Intake completed" },
    { k: "first_lane", label: "First Lane conversation" },
    { k: "any_activity", label: "Any activity ever" },
    { k: "routine", label: "Routine (3+ wks)" },
];

function Ret({ row, k }) {
    const n = row[k], d = row[`${k}_eligible`];
    if (!d) return <span className="text-[#9CA6A1]">—</span>;
    return <span>{Math.round((100 * n) / d)}% <span className="text-[#6E7A74]">({n}/{d})</span></span>;
}

export default function NumbersView() {
    const [state, setState] = useState({ loading: true, error: null, data: null });
    const [watch, setWatch] = useState(null);
    const [returning, setReturning] = useState(null);
    const load = () => {
        setState((s) => ({ ...s, loading: true, error: null }));
        api.get("/admin/numbers")
            .then((r) => setState({ loading: false, error: null, data: r.data }))
            .catch((e) => setState({ loading: false, error: e?.response?.data?.detail || "Failed to load", data: null }));
        api.get("/admin/guide-directory/status").then((r) => setWatch(r.data)).catch(() => setWatch(null));
    };
    useEffect(() => { load(); }, []);

    if (state.loading) return <div className="ml-card p-8 flex items-center gap-2 text-[#6E7A74]" data-testid="numbers-loading"><Loader2 size={16} className="animate-spin" /> Computing…</div>;
    if (state.error) return <div className="ml-card p-6 text-coral-dark" data-testid="numbers-error">{state.error} <button onClick={load} className="underline ml-2">Retry</button></div>;

    const d = state.data;
    const isProd = typeof window !== "undefined" && /memorylanecare\.net$/i.test(window.location.hostname);
    const den = d.denominator || {};
    const rec = den.reconciliation || {};
    const marker = d.deploy_marker || {};
    const markerDate = marker.at ? new Date(marker.at) : null;
    const markerWeek = markerDate ? `${markerDate.getUTCFullYear()}-W${String(getISOWeek(markerDate)).padStart(2, "0")}` : null;
    const weeks = d.weeks || [];
    // Marker sits before the first cohort week >= deploy week; if every
    // cohort predates the deploy, it renders after the last row.
    let markerIdx = markerWeek ? weeks.findIndex((w) => w.week >= markerWeek) : -1;
    if (markerWeek && markerIdx === -1) markerIdx = weeks.length;
    const straddles = markerIdx >= 0 && weeks[markerIdx]?.week === markerWeek;

    const MarkerRow = () => (
        <tr data-testid="numbers-deploy-marker">
            <td colSpan={11} className="px-4 py-2 bg-teal-soft/60 text-teal-dark text-xs font-semibold border-y border-teal/30">
                ▲ before · {marker.label} — {markerDate.toISOString().slice(0, 16).replace("T", " ")} UTC · after ▼
                {straddles ? " (this cohort week straddles the deploy)" : ""}
            </td>
        </tr>
    );

    return (
        <div className="space-y-5" data-testid="numbers-view">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <h2 className="font-heading font-bold text-2xl text-[#12776F] inline-flex items-center gap-3">
                        Numbers
                        <span data-testid="numbers-env-badge" className={`text-[10px] uppercase tracking-wider font-bold px-2.5 py-1 rounded-2xl ${isProd ? "bg-teal text-white" : "bg-[#ECA72C]/20 text-[#8A5A2B]"}`}>
                            {isProd ? "Production" : "Preview data"}
                        </span>
                    </h2>
                    <p className="text-sm text-[#6E7A74] mt-1" data-testid="numbers-denominator">
                        n = <strong className="text-[#1F2624]">{den.included}</strong> real caregivers · excluded: {Object.entries(den.excluded_counts || {}).map(([r, c]) => `${c} ${r}`).join(" · ") || "none"}
                    </p>
                </div>
                <button onClick={load} data-testid="numbers-refresh" className="ml-btn-secondary !min-h-[38px] !px-4 text-sm inline-flex items-center gap-1.5">
                    <RefreshCcw size={14} /> Refresh
                </button>
            </div>

            <div className="ml-card p-0 overflow-x-auto" data-testid="numbers-table-card">
                <table className="w-full text-sm" data-testid="numbers-table">
                    <thead>
                        <tr className="text-left text-[11px] uppercase tracking-wider text-[#6E7A74] border-b border-cream-dark">
                            <th className="px-4 py-3">Week</th>
                            {COLS.map((c) => <th key={c.k} className="px-3 py-3 whitespace-nowrap">{c.label}</th>)}
                            <th className="px-3 py-3">D1</th>
                            <th className="px-3 py-3">D7</th>
                            <th className="px-3 py-3">D30</th>
                        </tr>
                    </thead>
                    <tbody>
                        {weeks.map((row, i) => (
                            <React.Fragment key={row.week}>
                                {i === markerIdx && <MarkerRow />}
                                <tr className="border-t border-cream-dark" data-testid={`numbers-row-${row.week}`}>
                                    <td className="px-4 py-2.5 font-medium text-[#1F2624] whitespace-nowrap">{row.week}<span className="block text-[10px] text-[#9CA6A1]">{row.week_start}</span></td>
                                    {COLS.map((c) => <td key={c.k} className="px-3 py-2.5">{row[c.k]}</td>)}
                                    <td className="px-3 py-2.5 whitespace-nowrap"><Ret row={row} k="d1" /></td>
                                    <td className="px-3 py-2.5 whitespace-nowrap"><Ret row={row} k="d7" /></td>
                                    <td className="px-3 py-2.5 whitespace-nowrap"><Ret row={row} k="d30" /></td>
                                </tr>
                            </React.Fragment>
                        ))}
                        {markerIdx === weeks.length && <MarkerRow />}
                        <tr className="border-t-2 border-[#CBD5E0] font-semibold text-[#1F2624]" data-testid="numbers-totals-row">
                            <td className="px-4 py-2.5">All</td>
                            {COLS.map((c) => <td key={c.k} className="px-3 py-2.5">{d.totals?.[c.k] ?? 0}</td>)}
                            <td className="px-3 py-2.5 whitespace-nowrap"><Ret row={d.totals || {}} k="d1" /></td>
                            <td className="px-3 py-2.5 whitespace-nowrap"><Ret row={d.totals || {}} k="d7" /></td>
                            <td className="px-3 py-2.5 whitespace-nowrap"><Ret row={d.totals || {}} k="d30" /></td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div className="ml-card p-5 text-xs text-[#4C5551] space-y-1.5" data-testid="numbers-sources">
                <p className="font-semibold text-[#1F2624] text-sm">Sources & caveats</p>
                {Object.entries(d.sources || {}).map(([k, s]) => (
                    <p key={k}><strong className="capitalize">{k.replace(/_/g, " ")}</strong>: {s.source}{s.caveat ? <span className="text-[#8A5A2B]"> — {s.caveat}</span> : null}</p>
                ))}
                <p className="pt-2 border-t border-cream-dark text-[#6E7A74]" data-testid="numbers-reconciliation">
                    <strong className="text-[#1F2624]">Denominator reconciliation</strong>: all non-admin accounts = {rec.all_non_admin_accounts} · minus tester accounts = {rec.non_admin_no_tester_code} · minus YC demo + test-domain emails = <strong>{rec.canonical}</strong> (canonical). {rec.note}
                </p>
                <p className="text-[#9CA6A1]">Computed {d.computed_at?.slice(0, 16).replace("T", " ")} UTC. New metrics get added to this table — never a new page.</p>
                {watch && (
                    <p className={`pt-2 border-t border-cream-dark ${watch.new_count > 0 ? "text-[#8A5A2B] font-medium" : "text-[#6E7A74]"}`} data-testid="numbers-guide-watch">
                        <strong className="text-[#1F2624]">GUIDE directory watch</strong>:{" "}
                        {watch.checked_at
                            ? <>CMS list checked {watch.checked_at.slice(0, 10)} · {watch.count} participants{watch.cms_label ? ` (CMS: last updated ${watch.cms_label})` : ""} · {watch.new_count > 0
                                ? `⚑ ${watch.new_count} NEW since the in-app directory: ${(watch.new_names || []).slice(0, 8).map(n => n.name).join("; ")}${watch.new_count > 8 ? "…" : ""} — you were emailed the full list`
                                : "no new participants"}{watch.removed_count > 0 ? ` · ${watch.removed_count} removed` : ""}</>
                            : "first quarterly check hasn't run yet"}
                        {watch.last_error ? ` · last check error: ${watch.last_error}` : ""} (re-checks every {watch.check_every_days} days)
                    </p>
                )}
            </div>
            <details className="ml-card p-5 text-sm" data-testid="numbers-returning"
                onToggle={(e) => { if (e.currentTarget.open && !returning) {
                    api.get("/admin/numbers/returning").then((r) => setReturning(r.data)).catch(() => setReturning({ error: true }));
                } }}>
                <summary className="cursor-pointer font-semibold text-[#1F2624] select-none">
                    Returning users — who's coming back, and how often
                </summary>
                {!returning && <p className="mt-3 text-[#6E7A74]">Loading…</p>}
                {returning?.error && <p className="mt-3 text-coral-dark">Failed to load.</p>}
                {returning && !returning.error && (
                    <>
                        <p className="mt-2 text-xs text-[#6E7A74]">{returning.definition}</p>
                        {returning.count === 0 ? (
                            <p className="mt-3 text-[#6E7A74] italic" data-testid="numbers-returning-empty">No one has returned on a second day yet (with this environment's data).</p>
                        ) : (
                            <table className="mt-3 w-full text-sm">
                                <thead>
                                    <tr className="text-left text-[11px] uppercase tracking-wider text-[#6E7A74] border-b border-cream-dark">
                                        <th className="py-2 pr-3">Name</th>
                                        <th className="py-2 pr-3">Email</th>
                                        <th className="py-2 pr-3">Days used</th>
                                        <th className="py-2 pr-3">Actions</th>
                                        <th className="py-2 pr-3">Weeks</th>
                                        <th className="py-2">Last active</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {returning.users.map((u) => (
                                        <tr key={u.email} className="border-t border-cream-dark" data-testid="numbers-returning-row">
                                            <td className="py-2 pr-3 font-medium text-[#1F2624]">{u.name}</td>
                                            <td className="py-2 pr-3 text-[#4C5551]">{u.email}</td>
                                            <td className="py-2 pr-3">{u.active_days}</td>
                                            <td className="py-2 pr-3">{u.total_actions}</td>
                                            <td className="py-2 pr-3">{u.weeks_active}</td>
                                            <td className="py-2 text-[#6E7A74]">{u.last_active}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </>
                )}
            </details>
            <ActivationCard />
            <ShareLinksCard />
            <UploadAuditCard />
        </div>
    );
}

function ActivationCard() {
    const [data, setData] = useState(null);
    const [start, setStart] = useState("");
    const load = () => api.get("/admin/activation").then((r) => { setData(r.data); setStart((r.data.cohort_start || "").slice(0, 10)); }).catch(() => setData({ error: true }));
    const saveStart = async () => {
        if (!start) return;
        await api.put("/admin/activation/cohort-start", { since: `${start}T00:00:00+00:00` });
        await load();
    };
    const pct = (v) => (v == null ? "—" : `${Math.round(v * 100)}%`);
    return (
        <details className="ml-card p-5 text-sm" data-testid="numbers-activation"
            onToggle={(e) => { if (e.currentTarget.open && !data) load(); }}>
            <summary className="cursor-pointer font-semibold text-[#1F2624] select-none">
                Activation — post-deploy cohort only (signup → first meaningful action)
            </summary>
            {!data && <p className="mt-3 text-[#6E7A74]">Loading…</p>}
            {data?.error && <p className="mt-3 text-coral-dark">Failed to load.</p>}
            {data && !data.error && (
                <>
                    <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-[#6E7A74]">
                        <span>Cohort starts (deploy date):</span>
                        <input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="activation-cohort-start-input"
                            className="px-2 py-1 rounded-lg border border-cream-dark text-xs" />
                        <button onClick={saveStart} data-testid="activation-cohort-start-save"
                            className="rounded-2xl bg-teal-dark text-white font-semibold px-3 py-1 text-[11px] hover:opacity-90">Set</button>
                        <span className="italic">Pre-cohort caregivers are never counted or backfilled.</span>
                    </div>
                    <p className="mt-3 text-[#1F2624]" data-testid="activation-headline">
                        <strong>{data.activated}</strong> of <strong>{data.signups}</strong> cohort signups activated · <strong className="text-teal-dark">{pct(data.activation_rate)}</strong>
                    </p>
                    {(data.by_source || []).length > 0 && (
                        <div className="mt-3">
                            <p className="text-[11px] uppercase tracking-wider text-[#6E7A74] font-semibold mb-1">By first action (the actual front door)</p>
                            {data.by_source.map((s) => (
                                <p key={s.source} className="text-sm text-[#2A3330]" data-testid={`activation-source-${s.source}`}>
                                    {s.source.replace(/_/g, " ")}: <strong>{s.activated}</strong>
                                </p>
                            ))}
                        </div>
                    )}
                    <p className="mt-3 text-sm text-[#2A3330]" data-testid="activation-retention">
                        <span className="text-[11px] uppercase tracking-wider text-[#6E7A74] font-semibold">Retention (separate measure)</span><br />
                        {data.retention.returned_later_day} of {data.retention.activated} activated caregivers returned on a later day · <strong>{pct(data.retention.rate)}</strong>
                    </p>
                    {(data.caregivers || []).length > 0 && (
                        <details className="mt-3">
                            <summary className="cursor-pointer text-xs text-[#6E7A74]">Cohort caregivers ({data.caregivers.length})</summary>
                            <table className="mt-2 w-full text-xs">
                                <thead><tr className="text-left text-[10px] uppercase tracking-wider text-[#6E7A74] border-b border-cream-dark">
                                    <th className="py-1.5 pr-2">Email</th><th className="py-1.5 pr-2">Signed up</th><th className="py-1.5 pr-2">Activated</th><th className="py-1.5 pr-2">Via</th><th className="py-1.5">Returned</th>
                                </tr></thead>
                                <tbody>
                                    {data.caregivers.map((c) => (
                                        <tr key={c.email + c.signup} className="border-t border-cream-dark">
                                            <td className="py-1.5 pr-2">{c.email}</td>
                                            <td className="py-1.5 pr-2">{(c.signup || "").slice(0, 10)}</td>
                                            <td className="py-1.5 pr-2">{c.activated_at ? (c.activated_at || "").slice(0, 10) : <span className="text-[#9CA6A1]">not yet</span>}</td>
                                            <td className="py-1.5 pr-2">{(c.source || "—").replace(/_/g, " ")}</td>
                                            <td className="py-1.5">{c.activated_at ? (c.returned_later_day ? "yes" : "no") : "—"}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </details>
                    )}
                </>
            )}
        </details>
    );
}

function ShareLinksCard() {
    const [inv, setInv] = useState(null);
    const [rotating, setRotating] = useState(false);
    const load = () => api.get("/admin/share-links/inventory").then((r) => setInv(r.data)).catch(() => setInv({ error: true }));
    const totalActive = (inv?.surfaces || []).reduce((a, s) => a + (s.active || 0), 0);
    const rotate = async () => {
        if (!window.confirm(`Rotate (revoke) all ${totalActive} active share links? Old URLs stop working immediately — caregivers can mint fresh links from the app. This cannot be undone.`)) return;
        setRotating(true);
        try { await api.post("/admin/share-links/rotate"); await load(); } finally { setRotating(false); }
    };
    return (
        <details className="ml-card p-5 text-sm" data-testid="numbers-share-links"
            onToggle={(e) => { if (e.currentTarget.open && !inv) load(); }}>
            <summary className="cursor-pointer font-semibold text-[#1F2624] select-none">
                Share links — exposure inventory &amp; rotation
            </summary>
            <p className="mt-2 text-xs text-[#6E7A74]">
                Links minted BEFORE the analytics-redaction deploy may have had their tokens logged by
                GA/Clarity when opened. View logs don&apos;t record which token was opened, so the safe move
                is rotating them all: old URLs die instantly, caregivers re-share fresh links, and new
                links never reach analytics.
            </p>
            {!inv && <p className="mt-3 text-[#6E7A74]">Loading…</p>}
            {inv?.error && <p className="mt-3 text-coral-dark">Failed to load.</p>}
            {inv && !inv.error && (
                <>
                    <table className="mt-3 w-full text-sm">
                        <thead><tr className="text-left text-[11px] uppercase tracking-wider text-[#6E7A74] border-b border-cream-dark">
                            <th className="py-2 pr-3">Surface</th><th className="py-2 pr-3">Total minted</th><th className="py-2 pr-3">Still active</th><th className="py-2">Viewed ≥1×</th>
                        </tr></thead>
                        <tbody>
                            {inv.surfaces.map((s) => (
                                <tr key={s.surface} className="border-t border-cream-dark" data-testid={`share-links-row-${s.surface}`}>
                                    <td className="py-2 pr-3 font-medium text-[#1F2624]">{s.surface.replace(/_/g, " ")}</td>
                                    <td className="py-2 pr-3">{s.total}</td>
                                    <td className="py-2 pr-3">{s.active}</td>
                                    <td className="py-2">{s.viewed_at_least_once ?? "not logged per-link"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {totalActive > 0 && (
                        <button onClick={rotate} disabled={rotating} data-testid="share-links-rotate-btn"
                            className="mt-3 inline-flex items-center gap-2 rounded-2xl bg-coral-dark text-white font-semibold px-5 py-2 text-xs hover:opacity-90 disabled:opacity-50">
                            {rotating ? <Loader2 size={13} className="animate-spin" /> : null} Rotate all {totalActive} active links
                        </button>
                    )}
                    {totalActive === 0 && <p className="mt-2 text-teal-dark italic">No active share links — nothing to rotate.</p>}
                </>
            )}
        </details>
    );
}

function UploadAuditCard() {
    const [audit, setAudit] = useState(null);
    const [quarantining, setQuarantining] = useState(false);
    const load = () => api.get("/admin/care-records/audit").then((r) => setAudit(r.data)).catch(() => setAudit({ error: true }));
    const activeFlagged = (audit?.flagged || []).filter((f) => !f.is_deleted);
    const rowAction = async (f) => {
        if (f.is_deleted) {
            await api.post("/admin/care-records/restore", { ids: [f.id] });
        } else {
            if (!window.confirm(`Quarantine "${f.filename}"? Reversible — the original file is kept and you can restore it here.`)) return;
            await api.post("/admin/care-records/quarantine", { ids: [f.id] });
        }
        await load();
    };
    const quarantine = async () => {
        if (!window.confirm(`Quarantine ${activeFlagged.length} flagged file(s)? They will be hidden from caregivers (soft-deleted, originals kept in storage).`)) return;
        setQuarantining(true);
        try { await api.post("/admin/care-records/quarantine"); await load(); } finally { setQuarantining(false); }
    };
    return (
        <details className="ml-card p-5 text-sm" data-testid="numbers-upload-audit"
            onToggle={(e) => { if (e.currentTarget.open && !audit) load(); }}>
            <summary className="cursor-pointer font-semibold text-[#1F2624] select-none">
                Upload audit — files that would fail today's allowlist (PDF · JPG · PNG · HEIC)
            </summary>
            <p className="mt-2 text-xs text-[#6E7A74]">
                Checks every stored care-record file on THIS environment against the magic-byte allowlist.
                Stored files are never public: downloads require the owner's login and are always served as
                attachments (nosniff), so nothing can execute or render inline from a URL.
            </p>
            {!audit && <p className="mt-3 text-[#6E7A74]">Loading…</p>}
            {audit?.error && <p className="mt-3 text-coral-dark">Failed to load.</p>}
            {audit && !audit.error && (
                <>
                    <p className="mt-3 text-[#1F2624]" data-testid="upload-audit-counts">
                        <strong>{audit.total_documents}</strong> file(s) stored · <strong className={audit.flagged_count ? "text-coral-dark" : "text-teal-dark"}>{audit.flagged_count}</strong> flagged
                        {activeFlagged.length > 0 && <> · <strong className="text-coral-dark">{activeFlagged.length} still visible to caregivers</strong></>}
                    </p>
                    {audit.flagged_count === 0 && <p className="mt-2 text-[#6E7A74] italic">Clean — every stored file passes the allowlist.</p>}
                    {audit.flagged_count > 0 && (
                        <table className="mt-3 w-full text-sm">
                            <thead>
                                <tr className="text-left text-[11px] uppercase tracking-wider text-[#6E7A74] border-b border-cream-dark">
                                    <th className="py-2 pr-3">File</th>
                                    <th className="py-2 pr-3">Stored type</th>
                                    <th className="py-2 pr-3">Uploader</th>
                                    <th className="py-2 pr-3">Size</th>
                                    <th className="py-2 pr-3">Uploaded</th>
                                    <th className="py-2">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {audit.flagged.map((f) => (
                                    <tr key={f.id} className="border-t border-cream-dark" data-testid="upload-audit-row">
                                        <td className="py-2 pr-3 font-medium text-[#1F2624]">{f.filename}</td>
                                        <td className="py-2 pr-3 text-[#4C5551]">{f.mime || "unknown"}</td>
                                        <td className="py-2 pr-3 text-[#4C5551]">{f.uploader_email || f.user_id}</td>
                                        <td className="py-2 pr-3">{Math.max(1, Math.round((f.size || 0) / 1024))} KB</td>
                                        <td className="py-2 pr-3 text-[#6E7A74]">{(f.uploaded_at || "").slice(0, 10)}</td>
                                        <td className="py-2 pr-3">{f.is_deleted ? <span className="text-teal-dark font-medium">quarantined</span> : <span className="text-coral-dark font-medium">active</span>}</td>
                                        <td className="py-2">
                                            <button onClick={() => rowAction(f)} data-testid={`upload-audit-row-action-${f.id}`}
                                                className={`text-[11px] font-semibold underline ${f.is_deleted ? "text-teal-dark" : "text-coral-dark"}`}>
                                                {f.is_deleted ? "Restore" : "Quarantine"}
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                    {activeFlagged.length > 0 && (
                        <button onClick={quarantine} disabled={quarantining} data-testid="upload-audit-quarantine-btn"
                            className="mt-3 inline-flex items-center gap-2 rounded-2xl bg-coral-dark text-white font-semibold px-5 py-2 text-xs hover:opacity-90 disabled:opacity-50">
                            {quarantining ? <Loader2 size={13} className="animate-spin" /> : null} Quarantine all flagged files
                        </button>
                    )}
                </>
            )}
        </details>
    );
}

function getISOWeek(date) {
    const d = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
}
